/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidencAccession e any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import Select, { OnChangeValue } from "react-select";
import SimpleReactValidator from "simple-react-validator";
import {
  CommonDatePicker,
  DateRange,
  EquivalenceType,
  LaddaButton,
  normalizeDateRange,
  Switch,
} from "@advapacs/common";
import { OptionTypeBase } from "../../types/common";
import {
  defaultCodeManagementValue,
  ICodeManagementForm,
  ICodeMappingType,
  ICodeRequest,
  ICodeRvu,
  initialDefaultCodeMappingTypeState,
  initialDefaultCodeRvuState,
} from "../../types/codeManagement";
import { ErrorMessageValidator } from "../Displays/ErrorMessageValidator";
import { IErrorMessage } from "../../types/components/errorMessage";
import { getSelectStyles } from "../../constant";
import ReactSelectSearch from "../Select/ReactSelectSearch";
import { FormLabel } from "react-bootstrap";
import { SelectChangeFunction } from "../../types/components/reactSelectSearchProps";
import { InformationTooltip } from "../Tooltips/InformationTooltip";
import { createOptions, ItemWithNameAndId } from "../../helpers";
import debounce from "lodash.debounce";
import AHSAsyncSelect from "../Select/AHSAsyncSelect";

const typeOptions: OptionTypeBase[] = [
  {
    label: "HL7",
    value: "HL7",
  },
  {
    label: "FHIR",
    value: "FHIR",
  },
];

export const CodeManagementForm = forwardRef(
  (props: ICodeManagementForm, ref) => {
    const { t } = useTranslation();
    const {
      data,
      errorMessage,
      readonly,
      trigger = false,
      isDark = false,
      worklistSpace = false, // belong worklist location
      bodyPartOptions = null,
      qualificationOptions = [],
      codeTableOptions = [],
      lazyFetchCodes,
      dateFormat,
    } = props;

    const [, setChanged] = useState(null);
    const [entity, setEntity] = useState<ICodeRequest>(
      defaultCodeManagementValue
    );
    const [type, setType] = useState<"HL7" | "FHIR">("HL7");

    const debounceFetchCodes = lazyFetchCodes
      ? debounce(lazyFetchCodes, 500)
      : undefined;

    const toDateOnly = (val: any): Date | null => {
      if (!val) return null;
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    const validatorForm = useMemo(() => {
      return new SimpleReactValidator({
        messages: {
          required: `${t("form:validation.required")}`,
        },
        validators: {
          rvu: {
            message: `${t("form:validation.rvu_limit_decimal")}`,
            rule: (val: string) => {
              if (val === null || val === undefined) {
                return true;
              }
              const value = String(val).trim();

              if (value === "") {
                return true;
              }
              if (!/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(value)) {
                return false;
              }
              const mainPart = value.split(/[eE]/)[0];
              const decimalPart = mainPart.split(".")[1];

              if (decimalPart && decimalPart.length > 4) {
                return false;
              }
              const digits = mainPart.replace(/^[-+]/, "").replace(".", "");
              return digits.replace(/^0+/, "").length <= 18;
            },
          },
          uniqueCodeMapping: {
            message: t("form:code_management.duplicate_equivalent_code"),
            rule: (codeId: string) => {
              if (!codeId) {
                return true;
              }
              const count =
                entity?.codeMappings?.filter((x) => x.codeId === codeId)
                  .length ?? 0;
              return count <= 1;
            },
          },
          noEffectiveOverlap: {
            message: t("form:code_management.effective_date_overlap"),
            rule: (_val: string, params: any[]) => {
              const index = Number(params[0]);
              const codeRvus = entity?.codeRvus ?? [];
              const current = codeRvus[index]?.effective;

              for (let i = 0; i < codeRvus.length; i++) {
                if (i === index) continue;
                const other = codeRvus[i]?.effective;

                const curStart = toDateOnly(current?.startDate);
                const curEnd = toDateOnly(current?.endDate);
                const otherStart = toDateOnly(other?.startDate);
                const otherEnd = toDateOnly(other?.endDate);

                const overlap =
                  (otherEnd === null ||
                    curStart === null ||
                    curStart <= otherEnd) &&
                  (curEnd === null ||
                    otherStart === null ||
                    otherStart <= curEnd);

                if (overlap) return false;
              }
              return true;
            },
          },
          rvuRequiredWhenDateSet: {
            message: t("form:code_management.provide_rvu"),
            rule: (_val: string, params: DateRange[]) => {
              const index = Number(params[0]);
              const item = entity?.codeRvus?.[index];
              const hasDate =
                item?.effective?.startDate || item?.effective?.endDate;
              if (!hasDate) return true;
              return !!item?.value?.trim();
            },
          },
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, type, entity]);

    useEffect(() => {
      if (!data?.id) return;
      setEntity({
        ...data,
        enabled: data.enabled,
        reportingQualifications: createOptions(
          data.reportingQualifications as ItemWithNameAndId[]
        ),
        verifyingQualifications: createOptions(
          data.verifyingQualifications as ItemWithNameAndId[]
        ),
        codeTable: data.codeTable
          ? { value: data.codeTable?.id, label: data.codeTable?.name }
          : undefined,
        billingCodes: data?.billingCodes?.length
          ? data?.billingCodes.map((item: ICodeRequest) => ({
              value: item.id,
              label: item.display
                ? `${item.code} - ${item.display}`
                : item.code ?? "",
            }))
          : [],
        codeMappings: data?.codeMappings?.length
          ? data?.codeMappings?.map((item: ICodeMappingType) => ({
              codeId: item.code?.id ?? "",
              label: item.code?.display
                ? `${item.code.code} - ${item.code.display}`
                : item.code?.code ?? "",
              equivalence: item.equivalence ?? EquivalenceType.EXACT,
            }))
          : [initialDefaultCodeMappingTypeState],
        codeRvus: data?.codeRvus?.length
          ? data?.codeRvus?.map((item: ICodeRvu) => ({
              value: item.value ?? "",
              effective: {
                startDate: item.effective?.startDate || null,
                endDate: item.effective?.endDate || null,
              },
            }))
          : [initialDefaultCodeRvuState],
      });
      setType(data.system?.fhirSystem ? "FHIR" : "HL7");
    }, [data]);

    useEffect(() => {
      setChanged({}); // trigger to re-init Switch value when this form in modal when create/edit an order.
    }, [trigger]);

    useImperativeHandle(
      ref,
      () => ({
        validate() {
          return {
            validatorForm,
          };
        },
        getData() {
          const request =
            type === "HL7"
              ? {
                  ...entity,
                  system: {
                    ...entity.system,
                    fhirSystem: "",
                    fhirVersion: "",
                  },
                }
              : {
                  ...entity,
                  system: {
                    ...entity.system,
                    hl7Scheme: "",
                    hl7Version: "",
                  },
                };
          return {
            entity: { ...request, codeTableId: request?.codeTable?.value },
          };
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [validatorForm, entity]
    );

    const handleSelectChange: SelectChangeFunction = (newValue, actionMeta) => {
      const value = newValue;
      const name = actionMeta.name;
      setEntity({ ...entity, [name]: value });
    };

    const updateEffectiveDate = (rowIndex: number, effective: DateRange) => {
      setEntity((prev) => ({
        ...prev,
        codeRvus: prev.codeRvus?.map((item, i) =>
          i === rowIndex
            ? {
                ...item,
                effective,
              }
            : item
        ),
      }));
    };

    const optionEquivalenceType = useMemo(
      () => [
        {
          label: `${t("form:code_management.code_exact")}`,
          value: EquivalenceType.EXACT,
        },
        {
          label: `${t("form:code_management.code_wider")}`,
          value: EquivalenceType.WIDER,
        },
        {
          label: `${t("form:code_management.code_narrower")}`,
          value: EquivalenceType.NARROWER,
        },
      ],
      [t]
    );

    return (
      <>
        <div className="form-group row">
          <div className="col-md-3">
            <label htmlFor="code" className="form-label">
              {t("code")}
              {!worklistSpace && <span className="text-danger">*</span>}
            </label>
          </div>
          <div className="col-md-5">
            <input
              type="text"
              className="form-control"
              disabled={readonly || worklistSpace}
              value={entity.code}
              onChange={(e) => setEntity({ ...entity, code: e.target.value })}
            />
            <ErrorMessageValidator
              errorMessage={validatorForm.message(
                t("code"),
                entity.code,
                "required|max:16,string"
              )}
            />
            {errorMessage?.code === "422" && (
              <span className="text-danger">
                {(errorMessage as IErrorMessage)?.message}
              </span>
            )}
          </div>
        </div>

        <div className="form-group row">
          <div className="col-md-3">
            <label htmlFor="display" className="form-label">
              {t("display")}
              {!worklistSpace && <span className="text-danger">*</span>}
            </label>
          </div>
          <div className="col-md-5">
            <input
              type="text"
              className="form-control"
              value={entity.display}
              disabled={readonly || worklistSpace}
              onChange={(e) =>
                setEntity({ ...entity, display: e.target.value })
              }
            />
            {!worklistSpace && (
              <ErrorMessageValidator
                errorMessage={validatorForm.message(
                  t("display"),
                  entity.display,
                  "required"
                )}
              />
            )}
          </div>
        </div>

        <div className="form-group row">
          <div className="col-md-3">
            <label htmlFor="type">{t("type")}</label>
          </div>
          <div className="col-md-5">
            <Select
              styles={getSelectStyles(isDark)}
              name="type"
              options={typeOptions}
              isDisabled={readonly || worklistSpace}
              value={
                typeOptions.find((a) => a.value === type) as OptionTypeBase
              }
              onChange={(e) => {
                setType(e.value);
              }}
              isSearchable={false}
              placeholder={t("select")}
            />
            <ErrorMessageValidator
              errorMessage={validatorForm.message(t("type"), type, "required")}
            />
          </div>
        </div>

        {type === "HL7" && (
          <>
            <div className="form-group row">
              <div className="col-md-3">
                <label htmlFor="hl7Scheme" className="form-label">
                  {t("hl7:code_system.hl7_code_system")}
                  {!worklistSpace && <span className="text-danger">*</span>}
                </label>
              </div>
              <div className="col-md-5">
                <input
                  disabled={readonly || worklistSpace}
                  type="text"
                  className="form-control"
                  value={entity.system?.hl7Scheme}
                  onChange={(e) =>
                    setEntity({
                      ...entity,
                      system: { ...entity.system, hl7Scheme: e.target.value },
                    })
                  }
                />
                {!worklistSpace && (
                  <ErrorMessageValidator
                    errorMessage={validatorForm.message(
                      t("hl7:code_system.hl7_code_system"),
                      entity.system?.hl7Scheme,
                      "required"
                    )}
                  />
                )}
              </div>
            </div>

            <div className="form-group row">
              <div className="col-md-3">
                <label htmlFor="hl7Version" className="form-label">
                  {t("hl7:code_system.hl7_version")}
                </label>
              </div>
              <div className="col-md-5">
                <input
                  type="text"
                  className="form-control"
                  disabled={readonly || worklistSpace}
                  value={entity.system?.hl7Version}
                  onChange={(e) =>
                    setEntity({
                      ...entity,
                      system: { ...entity.system, hl7Version: e.target.value },
                    })
                  }
                />
              </div>
            </div>
          </>
        )}

        {type === "FHIR" && (
          <>
            <div className="form-group row">
              <div className="col-md-3">
                <label htmlFor="fhirSystem" className="form-label">
                  {t("hl7:code_system.fhir_code_system")}
                  {!worklistSpace && <span className="text-danger">*</span>}
                </label>
              </div>
              <div className="col-md-5">
                <input
                  type="text"
                  className="form-control"
                  value={entity.system?.fhirSystem}
                  disabled={readonly || worklistSpace}
                  onChange={(e) =>
                    setEntity({
                      ...entity,
                      system: { ...entity.system, fhirSystem: e.target.value },
                    })
                  }
                />
                {!worklistSpace && (
                  <ErrorMessageValidator
                    errorMessage={validatorForm.message(
                      t("hl7:code_system.fhir_code_system"),
                      entity.system?.fhirSystem,
                      "required"
                    )}
                  />
                )}
              </div>
            </div>

            <div className="form-group row">
              <div className="col-md-3">
                <label htmlFor="fhirVersion" className="form-label">
                  {t("hl7:code_system.fhir_version")}
                </label>
              </div>
              <div className="col-md-5">
                <input
                  type="text"
                  className="form-control"
                  value={entity.system?.fhirVersion}
                  disabled={readonly || worklistSpace}
                  onChange={(e) =>
                    setEntity({
                      ...entity,
                      system: { ...entity.system, fhirVersion: e.target.value },
                    })
                  }
                />
              </div>
            </div>
          </>
        )}

        {worklistSpace && (
          <>
            <div className="form-group row">
              <div className="col-md-3">
                <label htmlFor="bodyPart" className="form-label">
                  {t("hl7:code_management.body_part")}
                </label>
              </div>
              <div className="col-md-5">
                <ReactSelectSearch
                  name="bodyPart"
                  options={bodyPartOptions}
                  isDisabled={readonly}
                  isClearable
                  isSearchable
                  value={
                    bodyPartOptions.find(
                      (a) => a.value === entity.bodyPart?.id
                    ) as OptionTypeBase
                  }
                  handleSelectChange={(e: OptionTypeBase) => {
                    setEntity({
                      ...entity,
                      bodyPart: {
                        id: e?.value,
                      },
                    });
                  }}
                  placeholder={t("select")}
                  isDark={isDark}
                />
              </div>
            </div>

            {entity?.codeRvus?.map((item, index) => {
              return (
                <>
                  <div className="form-group row">
                    <span className="col-md-3">
                      {index === 0 && <>{t("hl7:code_management.rvu")}</>}
                    </span>
                    <div className="rvu-date-range-column">
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9.]*"
                        name={`${new Date().getTime().toString()}_rvu`}
                        className="form-control"
                        placeholder="Enter a number"
                        value={item?.value ?? ""}
                        readOnly={readonly}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!/^[0-9.]*$/.test(val)) {
                            return;
                          }

                          const finalItems = entity?.codeRvus?.map((x, i) => {
                            if (index === i) {
                              return {
                                ...x,
                                value: val === "" ? undefined : val,
                              };
                            }
                            return x;
                          });
                          setEntity({
                            ...entity,
                            codeRvus: finalItems,
                          });
                        }}
                      />
                      <ErrorMessageValidator
                        errorMessage={validatorForm.message(
                          `rvu_${index}`,
                          item.value,
                          "rvu"
                        )}
                      />
                      <ErrorMessageValidator
                        errorMessage={validatorForm.message(
                          `rvu_required_${index}`,
                          String(index),
                          `rvuRequiredWhenDateSet:${index}`
                        )}
                      />
                    </div>
                    <div className="rvu-date-range-column">
                      <CommonDatePicker
                        selected={
                          item?.effective?.startDate
                            ? new Date(item.effective.startDate)
                            : undefined
                        }
                        dateFormat={dateFormat}
                        readOnly={readonly}
                        placeholderText={t("form:start_date")}
                        onChange={(startDate) => {
                          if (startDate) startDate.setHours(0, 0, 0);
                          updateEffectiveDate(
                            index,
                            normalizeDateRange(
                              startDate,
                              item?.effective?.endDate ?? null
                            )
                          );
                        }}
                        unfold={false}
                        localName={"enUS"}
                      />
                      <ErrorMessageValidator
                        errorMessage={validatorForm.message(
                          `CodeRvu_effective_${index}`,
                          String(index),
                          `noEffectiveOverlap:${index}`
                        )}
                      />
                    </div>
                    <div className="rvu-date-range-column">
                      <CommonDatePicker
                        selected={
                          item?.effective?.endDate
                            ? new Date(item.effective.endDate)
                            : undefined
                        }
                        dateFormat={dateFormat}
                        readOnly={readonly}
                        placeholderText={t("form:end_date")}
                        onChange={(endDate) => {
                          if (endDate) endDate.setHours(23, 59, 59);
                          updateEffectiveDate(
                            index,
                            normalizeDateRange(
                              item?.effective?.startDate ?? null,
                              endDate
                            )
                          );
                        }}
                        unfold={false}
                        localName={"enUS"}
                      />
                    </div>
                    {!readonly && entity?.codeRvus?.length > 1 && (
                      <div className="mr-3 mt-1 align-items-center">
                        <i
                          aria-hidden
                          onClick={() => {
                            const finalItems = [
                              ...entity?.codeRvus.slice(0, index),
                              ...entity?.codeRvus.slice(index + 1),
                            ];
                            setEntity({
                              ...entity,
                              codeRvus: finalItems,
                            });
                          }}
                          className="icon-trash text-danger cursor-pointer"
                        />
                      </div>
                    )}
                    {!readonly && entity?.codeRvus?.length - 1 === index && (
                      <div className="col-lg-2">
                        <LaddaButton
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClickHandler={() => {
                            setEntity({
                              ...entity,
                              codeRvus: [
                                ...entity.codeRvus,
                                initialDefaultCodeRvuState,
                              ],
                            });
                          }}
                          btnName={t("common:button.add_new")}
                        />
                      </div>
                    )}
                  </div>
                </>
              );
            })}

            <div className="form-group row">
              <FormLabel className="col-lg-3 col-form-label">
                {t("qualifications.reporting_qualifications")}
                <InformationTooltip
                  className="m-l-xxs"
                  title={t("qualifications.qualifications_tooltip")}
                />
              </FormLabel>
              <div className="col-lg-5">
                <ReactSelectSearch
                  isSearchable
                  isClearable
                  placeholder={t("select")}
                  placeholderReadonly={t("select")}
                  name="reportingQualifications"
                  isDisabled={readonly}
                  options={qualificationOptions}
                  handleSelectChange={handleSelectChange}
                  value={entity.reportingQualifications}
                  isMulti
                  isDark={isDark}
                />
              </div>
            </div>
            <div className="form-group row">
              <FormLabel className="col-lg-3 col-form-label">
                {t("qualifications.verifying_qualifications")}
                <InformationTooltip
                  className="m-l-xxs"
                  title={t("qualifications.qualifications_tooltip")}
                />
              </FormLabel>
              <div className="col-lg-5">
                <ReactSelectSearch
                  isSearchable
                  isClearable
                  placeholder={t("select")}
                  placeholderReadonly={t("select")}
                  name="verifyingQualifications"
                  isDisabled={readonly}
                  options={qualificationOptions}
                  handleSelectChange={handleSelectChange}
                  value={entity.verifyingQualifications}
                  isMulti
                  isDark={isDark}
                />
              </div>
            </div>
          </>
        )}
        {!worklistSpace && codeTableOptions?.length > 1 && (
          <div className="form-group row">
            <span className="col-md-3">
              {t("hl7:code_management.code_table")}
              <span className="text-danger">*</span>
            </span>
            <div className="col-md-5">
              <ReactSelectSearch
                name="codeTable"
                placeholder={t("select")}
                isSearchable
                isClearable
                value={
                  codeTableOptions.find(
                    (a) => a.value === entity?.codeTable?.value
                  ) as OptionTypeBase
                }
                isDisabled={readonly}
                options={
                  codeTableOptions?.map((item) => {
                    return item;
                  }) as OptionTypeBase[]
                }
                handleSelectChange={handleSelectChange}
                isDark={isDark}
              />
              <ErrorMessageValidator
                errorMessage={validatorForm.message(
                  t("hl7:code_management.code_table"),
                  entity?.codeTable?.value,
                  "required"
                )}
              />
            </div>
          </div>
        )}

        {!worklistSpace && (
          <>
            <div className="form-group row">
              <span className="col-md-3">
                {t("form:code_management.billing_codes")}
              </span>
              <div className="col-md-5">
                <AHSAsyncSelect
                  isMulti
                  styles={getSelectStyles(isDark)}
                  isDark={isDark}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  id="billingCodes"
                  name="billingCodes"
                  classNamePrefix="form-control-select"
                  loadOptions={debounceFetchCodes}
                  defaultOptions
                  // @ts-ignore
                  onChange={handleSelectChange}
                  isDisabled={readonly}
                  placeholder={t("form:type_to_search")}
                  noOptionsMessage={() => null}
                  value={entity.billingCodes}
                  isClearable
                />
              </div>
            </div>
            {entity?.codeMappings?.map((item, index) => {
              return (
                <>
                  <div className="form-group row">
                    <span className="col-md-3">
                      {index === 0 && (
                        <>{t("form:code_management.equivalent_codes")}</>
                      )}
                    </span>
                    <div className="col-md-2">
                      <ReactSelectSearch
                        placeholder={t("form:select_type")}
                        name="status"
                        isDisabled={readonly}
                        options={optionEquivalenceType}
                        isDark={isDark}
                        handleSelectChange={(
                          newValue: OnChangeValue<OptionTypeBase, false>
                        ) => {
                          const finalItems = entity?.codeMappings?.map(
                            (x, i) => {
                              if (index === i) {
                                return {
                                  ...x,
                                  equivalence: newValue?.value,
                                };
                              }
                              return x;
                            }
                          );
                          setEntity({
                            ...entity,
                            codeMappings: finalItems,
                          });
                        }}
                        value={
                          optionEquivalenceType.find(
                            (i) => i.value === item.equivalence
                          ) || null
                        }
                        isClearable={false}
                        isSearchable
                      />
                    </div>
                    <div className="col-md-3">
                      <AHSAsyncSelect
                        styles={getSelectStyles(isDark)}
                        isDark={isDark}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        id={`${new Date().getTime().toString()}_CodeId`}
                        name={`${new Date().getTime().toString()}_CodeId`}
                        classNamePrefix="form-control-select"
                        loadOptions={debounceFetchCodes}
                        defaultOptions
                        // @ts-ignore
                        onChange={(
                          newValue: OnChangeValue<OptionTypeBase, false>
                        ) => {
                          const finalItem = entity?.codeMappings?.map(
                            (x, i) => {
                              if (index == i) {
                                return {
                                  ...x,
                                  codeId: newValue?.value,
                                  label: newValue?.label,
                                };
                              }
                              return x;
                            }
                          );
                          setEntity({
                            ...entity,
                            codeMappings: finalItem,
                          });
                        }}
                        isDisabled={readonly}
                        placeholder={t("form:type_to_search")}
                        noOptionsMessage={() => null}
                        value={item.codeId ? item : undefined}
                        isClearable
                      />
                      <ErrorMessageValidator
                        errorMessage={validatorForm.message(
                          `EquivalentCode_${index}`,
                          item.codeId,
                          "uniqueCodeMapping"
                        )}
                      />
                    </div>
                    {!readonly && entity?.codeMappings?.length > 1 && (
                      <div className="mr-3 d-flex align-items-center">
                        <i
                          aria-hidden
                          onClick={() => {
                            const finalItems = [
                              ...entity?.codeMappings.slice(0, index),
                              ...entity?.codeMappings.slice(index + 1),
                            ];
                            setEntity({
                              ...entity,
                              codeMappings: finalItems,
                            });
                          }}
                          className="icon-trash text-danger cursor-pointer"
                        />
                      </div>
                    )}
                    {!readonly &&
                      entity?.codeMappings?.length - 1 === index && (
                        <div className="col-lg-2">
                          <LaddaButton
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClickHandler={() => {
                              setEntity({
                                ...entity,
                                codeMappings: [
                                  ...entity.codeMappings,
                                  initialDefaultCodeMappingTypeState,
                                ],
                              });
                            }}
                            btnName={t("common:button.add_new")}
                          />
                        </div>
                      )}
                  </div>
                </>
              );
            })}
          </>
        )}

        <div className="form-group row">
          <span className="col-form-label col-lg-3 col-xs-12">
            {t("enabled")}
          </span>
          <div className="col-lg-4 col-xs-12">
            <div className="form-check form-check-switch form-check-switch-left">
              <Switch
                value={entity.enabled}
                onText={t("enabled")}
                animate
                offText={t("disabled")}
                onChange={(event, state) =>
                  setEntity({
                    ...entity,
                    enabled: !!state,
                  })
                }
                disabled={readonly || worklistSpace}
              />
            </div>
          </div>
        </div>
      </>
    );
  }
);
